import React, { useEffect, useState } from 'react';
import { CheckCircle2, Eye, EyeOff, Mail } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useMarketplaceAuth } from './MarketplaceAuthContext';
import MarketplaceShell from './MarketplaceShell';
import { MktButton, Eyebrow } from './marketplaceUi';
import { getSubCategories, getUseCases, mktSignup, mktResendOtp, mktForgotPassword, mktResetPassword } from './marketplaceApi';
import { TERMS_URL, PRIVACY_URL, PARTICIPANT_LABEL } from './marketplaceLinks';

type Mode = 'login' | 'signup' | 'verify' | 'forgot' | 'reset';

const PASSWORD_MIN_HINT = 'Min 6 characters';
/** The normal, un-gated entry into the run flow. Any other protected path that
 * renders this gate is a deep link hit while logged out (see #58). */
const ENTRY_PATHS = ['/marketplace/run', '/marketplace'];

const inputCls = 'w-full rounded-lg border border-black/20 px-3 py-2.5 text-[14px] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20';

/**
 * Labelled field. Defined at module scope (NOT inside the component) so React
 * keeps the input mounted across keystrokes. Auto-applies an aria-label from the
 * field label, since the placeholders are example values.
 */
const Field: React.FC<{ children: React.ReactNode; label: string; hint?: string }> = ({ children, label: l, hint }) => (
  <div className="flex flex-col gap-1.5">
    <span className="flex items-center justify-between">
      <span className="text-[12px] text-[#6b6b6b]">{l}</span>
      {hint && <span className="text-[11px] text-[#999]">{hint}</span>}
    </span>
    {React.isValidElement(children)
      ? React.cloneElement(children as React.ReactElement, {
          'aria-label': (children as React.ReactElement).props['aria-label'] || l,
        })
      : children}
  </div>
);

/**
 * Password input with a show/hide toggle. Module scope (same reason as Field)
 * so the input stays mounted across keystrokes. The `aria-label` Field clones
 * in lands on this component and is forwarded to the real <input>; the toggle
 * itself is a non-submitting button excluded from the tab order, so Tab still
 * runs straight down the form.
 */
const PasswordInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  /** 'new-password' on signup/reset, 'current-password' on login — without it a
   *  password manager can neither autofill nor offer to save. */
  autoComplete: 'new-password' | 'current-password';
  'aria-label'?: string;
}> = ({ value, onChange, autoComplete, 'aria-label': ariaLabel }) => {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;
  return (
    <div className="relative">
      <input
        className={`${inputCls} pr-10`}
        type={visible ? 'text' : 'password'}
        placeholder="••••••••"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        name="password"
        autoComplete={autoComplete}
        aria-label={ariaLabel}
        required
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-[#9a9a9a] transition-colors hover:text-[#111]"
      >
        <Icon className="h-4 w-4" />
      </button>
    </div>
  );
};

/**
 * Coerce any backend error shape into a readable string so we never render an
 * object as a React child (FastAPI 422 returns an array of {msg, loc}).
 */
function toMessage(value: unknown, fallback = 'Something went wrong'): string {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const msgs = value.map((v) => toMessage(v, '')).filter(Boolean);
    return msgs.length ? msgs.join('; ') : fallback;
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    return toMessage(o.detail ?? o.error ?? o.message ?? o.msg, fallback);
  }
  return String(value);
}

/**
 * Gates the "run a study" flow behind the EXISTING platform auth (business email
 * enforced server-side). Ported from account_signup.html: two-column sign-up
 * with a "what you get / from the study" side panel, a confirmation card, and a
 * compact login. Browsing stays public; only running is gated.
 */
const MarketplaceGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading: authLoading, login, verifyOtp } = useMarketplaceAuth();
  const location = useLocation();
  // A protected deep link (e.g. /marketplace/done) rendered while logged out —
  // not the normal signup entry. Land on login with a clear reason (#58).
  const isDeepLink = !ENTRY_PATHS.includes(location.pathname);
  const requestedMode = (location.state as { mode?: Mode } | null)?.mode;
  const [mode, setMode] = useState<Mode>(
    requestedMode === 'login' || isDeepLink ? 'login' : 'signup',
  );
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState<{ subs: number; archetypes: number; useCases: number } | null>(null);
  // Public catalog stats for the signup side panel (best-effort). The headline
  // participant number is the fixed PARTICIPANT_LABEL marketing figure (#16/#53).
  useEffect(() => {
    Promise.all([getSubCategories(), getUseCases()])
      .then(([subs, ucs]) => setStats({
        subs: subs.length,
        archetypes: subs.reduce((n, s) => n + s.archetype_count, 0),
        useCases: ucs.length,
      }))
      .catch(() => { /* panel just hides its numbers */ });
  }, []);

  // While the session is still being checked, show a loader — NOT the signup
  // form. Otherwise a reload / post-run navigation briefly flashes the login or
  // signup page before auth resolves (#17).
  if (authLoading) {
    return (
      <MarketplaceShell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
        </div>
      </MarketplaceShell>
    );
  }

  if (isAuthenticated) return <>{children}</>;

  // Marketplace signup (separate from the platform /signup) — creates an
  // account_type='marketplace' user and sends an OTP.
  const doSignup = async () => {
    const name = `${firstName} ${lastName}`.trim();
    if (name.length < 2) { setError('Please enter your name.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    const { ok, data } = await mktSignup({ name, email, password, company, policies_accepted: true });
    if (ok) { setMode('verify'); setInfo(null); return; }
    setError(toMessage(data?.detail ?? data?.error ?? data?.message, 'Registration failed'));
  };

  const doResend = async () => {
    if (resending) return; // guard rapid double-clicks
    setResending(true);
    setError(null); setInfo(null);
    try {
      const { ok, data } = await mktResendOtp(email);
      if (ok) setInfo('A new code is on its way — check your inbox.');
      else setError(toMessage(data, 'Could not resend the code.'));
    } catch (e) {
      setError(toMessage(e, 'Could not resend the code.'));
    } finally {
      setResending(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (mode === 'signup') {
        await doSignup();
      } else if (mode === 'login') {
        const res = await login(email, password);
        if (!res.success) {
          if (res.requiresVerification) { setMode('verify'); setInfo('Please verify your email. Enter the code we sent you.'); }
          else setError(toMessage(res.message, 'Log in failed'));
        }
      } else if (mode === 'forgot') {
        // Always succeeds server-side (no account enumeration) → move to reset.
        await mktForgotPassword(email);
        setMode('reset');
        setInfo('If that account exists, we sent a reset code. Enter it below with your new password.');
      } else if (mode === 'reset') {
        if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
        const { ok, data } = await mktResetPassword(email, otp, newPassword);
        if (!ok) { setError(toMessage(data?.detail ?? data, 'Could not reset your password.')); return; }
        setMode('login');
        setInfo('Password reset. Log in with your new password.');
        setPassword(''); setOtp(''); setNewPassword('');
      } else {
        // verify-otp establishes the session and returns the user (the context
        // sets it), so no redundant second login() is needed.
        const res = await verifyOtp(email, otp);
        if (!res.success) { setError(toMessage(res.message, 'Verification failed')); return; }
      }
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const benefits = [
    ['One free study total', 'Pick any one: chat, survey, concept test, or packaging test'],
    ['One audience Segment', 'Pick any Segment across all 6 Breakfast Categories'],
    ['Real behavioral depth', `Twins built from ${PARTICIPANT_LABEL} real human surveys — not synthetic proxies`],
    ['Results in minutes', 'No scheduling, no recruitment, no waiting'],
  ];

  const messages = (
    <>
      {isDeepLink && mode === 'login' && !error && !info && (
        <div className="text-[13px] text-[#374151]">Please sign in to continue.</div>
      )}
      {error && <div className="text-[13px] text-red-600" role="alert">{error}</div>}
      {info && <div className="text-[13px] text-[#374151]" role="status" aria-live="polite">{info}</div>}
    </>
  );

  // ── Sign-up: two-column (form + benefits/stats panel) ──────────────
  if (mode === 'signup') {
    return (
      <MarketplaceShell>
        <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
          <div className="flex flex-col gap-4 rounded-xl border border-black/10 bg-white p-7">
            <div>
              <h1 className="text-[20px] font-medium text-[#111]">Create your free account</h1>
              <p className="mt-1 text-[13px] leading-relaxed text-[#6b6b6b]">Run your first use case against any AI Consumer Twin — free, no credit card needed.</p>
            </div>

            <form onSubmit={submit} className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2.5">
                <Field label="First name"><input className={inputCls} name="given-name" autoComplete="given-name" placeholder="Sarah" value={firstName} onChange={(e) => setFirstName(e.target.value)} required /></Field>
                <Field label="Last name"><input className={inputCls} name="family-name" autoComplete="family-name" placeholder="Chen" value={lastName} onChange={(e) => setLastName(e.target.value)} /></Field>
              </div>
              <Field label="Work email"><input className={inputCls} type="email" name="email" autoComplete="email" placeholder="sarah@magicspoon.com" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
              <Field label="Password" hint={PASSWORD_MIN_HINT}><PasswordInput value={password} onChange={setPassword} autoComplete="new-password" /></Field>
              <Field label="Company" hint="Optional"><input className={inputCls} name="organization" autoComplete="organization" placeholder="Magic Spoon" value={company} onChange={(e) => setCompany(e.target.value)} /></Field>

              {messages}

              <div className="flex flex-col gap-2.5">
                <MktButton type="submit" fullWidth disabled={busy}>{busy ? 'Please wait…' : 'Create free account →'}</MktButton>
                <p className="text-center text-[11px] leading-relaxed text-[#999]">
                  By signing up you agree to our{' '}
                  <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-primary underline">terms of service</a>{' '}and{' '}
                  <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-primary underline">privacy policy</a>.
                </p>
              </div>
            </form>

            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-black/10" />
              <span className="text-[12px] text-[#999]">already have an account?</span>
              <div className="h-px flex-1 bg-black/10" />
            </div>
            <MktButton variant="secondary" fullWidth onClick={() => { setMode('login'); setError(null); }}>Log in</MktButton>
          </div>

          {/* Side panel */}
          <div className="flex flex-col gap-3">
            <div className="rounded-xl border border-black/10 bg-white p-5">
              <Eyebrow className="mb-3.5">What you get for free</Eyebrow>
              <div className="flex flex-col">
                {benefits.map(([title, desc], i) => (
                  <div key={title} className={`flex items-start gap-2.5 py-2.5 ${i < benefits.length - 1 ? 'border-b border-black/10' : ''}`}>
                    <CheckCircle2 className="mt-0.5 h-[17px] w-[17px] flex-shrink-0 text-emerald-600" />
                    <div>
                      <div className="text-[13px] font-medium text-[#111]">{title}</div>
                      <div className="text-[12px] leading-snug text-[#6b6b6b]">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {stats && (
              <div className="rounded-xl border border-black/10 bg-white p-5">
                <Eyebrow className="mb-2.5">From the study</Eyebrow>
                <div className="flex flex-col gap-1.5">
                  <StatRow label="Participants surveyed" value={PARTICIPANT_LABEL} />
                  <StatRow label="Breakfast categories" value={stats.subs} />
                  <StatRow label="Segments" value={stats.archetypes} />
                  <StatRow label="Use cases available" value={stats.useCases} />
                </div>
              </div>
            )}
          </div>
        </div>
      </MarketplaceShell>
    );
  }

  // ── Verify: confirmation card + "what happens next" ────────────────
  if (mode === 'verify') {
    const steps = [
      ['Confirm your email', 'Enter the code we just sent to activate your account'],
      ['Choose your use case', 'Chat, survey, concept test, or packaging — you pick one'],
      ['Pick your audience Segment', 'Select a Breakfast Category and Segment to run against'],
      ['Get results', 'Real behavioral responses from your Twin Segment, in minutes'],
    ];
    return (
      <MarketplaceShell>
        <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
          <div className="rounded-xl border border-black/10 bg-white p-7">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle2 className="h-[22px] w-[22px] text-emerald-600" />
            </div>
            <h1 className="text-[20px] font-medium text-[#111]">You're in{firstName ? `, ${firstName}` : ''}</h1>
            <p className="mt-1.5 text-[13px] leading-relaxed text-[#6b6b6b]">
              We sent a confirmation code to <span className="font-medium text-[#111]">{email || 'your email'}</span>. Enter it below to activate your account.
            </p>

            <div className="mb-5 mt-5 rounded-xl bg-[#f5f5f3] p-4">
              <Eyebrow className="mb-3">Your account</Eyebrow>
              <div className="flex flex-col gap-2">
                <StatRow label="Company" value={company || '—'} />
                <StatRow label="Free studies remaining" value={<span className="text-emerald-700">1 of 1</span>} />
              </div>
            </div>

            <form onSubmit={submit} className="flex flex-col gap-3">
              <Field label="Confirmation code"><input className={inputCls} name="one-time-code" autoComplete="one-time-code" inputMode="numeric" placeholder="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value)} required /></Field>
              {messages}
              <MktButton type="submit" fullWidth disabled={busy}>{busy ? 'Please wait…' : 'Confirm & continue →'}</MktButton>
            </form>

            <div className="mt-4 text-[13px] text-[#6b6b6b]">
              Didn’t get the code?{' '}
              <button type="button" className="font-medium text-primary underline" disabled={resending} onClick={doResend}>Resend the confirmation</button>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <div className="rounded-xl border border-black/10 bg-white p-5">
              <Eyebrow className="mb-3.5">What happens next</Eyebrow>
              <div className="flex flex-col gap-3.5">
                {steps.map(([title, desc], i) => (
                  <div key={title} className="flex items-start gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#E8FCFA] text-[11px] font-medium text-[#0B7C74]">{i + 1}</div>
                    <div>
                      <div className="text-[13px] font-medium text-[#111]">{title}</div>
                      <div className="text-[12px] leading-snug text-[#6b6b6b]">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-start gap-2.5 rounded-xl bg-[#f5f5f3] p-4">
              <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#6b6b6b]" />
              <div>
                <div className="mb-0.5 text-[13px] font-medium text-[#111]">Didn't get the email?</div>
                <div className="text-[12px] leading-snug text-[#6b6b6b]">
                  Check your spam folder, or{' '}
                  <button type="button" className="text-primary underline" disabled={resending} onClick={doResend}>resend the confirmation</button>.
                </div>
              </div>
            </div>
          </div>
        </div>
      </MarketplaceShell>
    );
  }

  // ── Forgot / reset password: compact single card ──────────────────
  if (mode === 'forgot' || mode === 'reset') {
    const isReset = mode === 'reset';
    return (
      <MarketplaceShell maxWidth="max-w-[420px]">
        <div className="flex flex-col gap-4 rounded-xl border border-black/10 bg-white p-7">
          <div>
            <h1 className="text-[20px] font-medium text-[#111]">{isReset ? 'Reset your password' : 'Forgot your password?'}</h1>
            <p className="mt-1 text-[13px] leading-relaxed text-[#6b6b6b]">
              {isReset
                ? 'Enter the code we emailed you and choose a new password.'
                : 'Enter your work email and we’ll send you a reset code.'}
            </p>
          </div>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <Field label="Work email"><input className={inputCls} type="email" name="email" autoComplete="email" placeholder="sarah@magicspoon.com" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
            {isReset && (
              <>
                <Field label="Reset code"><input className={inputCls} name="one-time-code" autoComplete="one-time-code" inputMode="numeric" placeholder="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value)} required /></Field>
                <Field label="New password" hint={PASSWORD_MIN_HINT}><PasswordInput value={newPassword} onChange={setNewPassword} autoComplete="new-password" /></Field>
              </>
            )}
            {messages}
            <MktButton type="submit" fullWidth disabled={busy}>{busy ? 'Please wait…' : isReset ? 'Reset password →' : 'Send reset code →'}</MktButton>
          </form>
          <button type="button" className="text-center text-[12px] text-primary underline" onClick={() => { setMode('login'); setError(null); setInfo(null); }}>Back to log in</button>
        </div>
      </MarketplaceShell>
    );
  }

  // ── Login: compact single card ─────────────────────────────────────
  return (
    <MarketplaceShell maxWidth="max-w-[420px]">
      <div className="flex flex-col gap-4 rounded-xl border border-black/10 bg-white p-7">
        <div>
          <h1 className="text-[20px] font-medium text-[#111]">Welcome back</h1>
          <p className="mt-1 text-[13px] leading-relaxed text-[#6b6b6b]">Log in to your BluePill account to continue.</p>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field label="Work email"><input className={inputCls} type="email" name="email" autoComplete="email" placeholder="sarah@magicspoon.com" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
          <Field label="Password"><PasswordInput value={password} onChange={setPassword} autoComplete="current-password" /></Field>
          <div className="-mt-1 text-right">
            <button type="button" className="text-[12px] font-medium text-primary underline" onClick={() => { setMode('forgot'); setError(null); setInfo(null); }}>Forgot password?</button>
          </div>
          {messages}
          <MktButton type="submit" fullWidth disabled={busy}>{busy ? 'Please wait…' : 'Log in →'}</MktButton>
        </form>
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-black/10" />
          <span className="text-[12px] text-[#999]">don't have an account?</span>
          <div className="h-px flex-1 bg-black/10" />
        </div>
        <MktButton variant="secondary" fullWidth onClick={() => { setMode('signup'); setError(null); }}>Create free account</MktButton>
      </div>
    </MarketplaceShell>
  );
};

const StatRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-center justify-between text-[13px]">
    <span className="text-[#6b6b6b]">{label}</span>
    <span className="font-medium text-[#111]">{value}</span>
  </div>
);

export default MarketplaceGate;
